<template>
  <div class="operator-view-container  px-10">
    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="d-flex justify-center align-center" style="min-height: 300px;">
      <div class="text-center">
        <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
        <h3 class="mb-2">{{ $t('profiles.operatorView.loadError.title') }}</h3>
        <p class="text--secondary mb-4">{{ error.message || error }}</p>
        <v-btn color="primary" @click="loadOperator">{{ $t('profiles.operatorView.loadError.retryBtn') }}</v-btn>
      </div>
    </div>

    <!-- Main Content - Only show when operator data is loaded -->
    <div v-else-if="operator">
      <FlyoutMenuHead :selectedItem="selectedItem" type="operator" class="mb-3"/>
      <!-- Header -->
      <div class="operator-content scrollable-container">
        <!-- Main Info Card (first block like design) -->
        <v-card class="panel-card" flat>
        <v-card-text class="panel-inner">
          <div class="section-title">{{ $t('profiles.operatorView.sections.mainInfo') }}</div>

          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <div class="field">
                <div class="field-label">{{ $t('profiles.operatorView.fields.firstName') }}</div>
                <div class="field-value">{{ operator.first_name || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.dateOfBirth') }}</div>
                <div class="field-value">{{ formattedDateOfBirth || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.email') }}</div>
                <div class="field-value">{{ operator.email || '—' }}</div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="field">
                <div class="field-label">{{ $t('profiles.operatorView.fields.lastName') }}</div>
                <div class="field-value">{{ operator.last_name || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.gender') }}</div>
                <div class="field-value">{{ operator.gender || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.phoneNumber') }}</div>
                <div class="field-value">{{ operator.mobile_phone || '—' }}</div>
              </div>
            </v-col>
          </v-row>
           <v-row>
            <v-col>
              <div class="field">
                <div class="field-label">{{ $t('profiles.operatorView.fields.username') }}</div>
                <div class="field-value">{{ operator.username || '—' }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Additional details block (mimics big middle card in design) -->
      <v-card class="panel-card mt-6" flat>
        <v-card-text class="panel-inner">
          <div class="section-title">{{ $t('profiles.operatorView.sections.additionalDetails') }}</div>

          <v-row class="mt-4">
            <v-col cols="12" md="6">
              <div class="field">
                <div class="field-label">{{ $t('profiles.operatorView.fields.status') }}</div>
                <div class="field-value">{{ operator.enabled ? $t('profiles.operatorView.values.enabled') : $t('profiles.operatorView.values.disabled') }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.visionId') }}</div>
                <div class="field-value">{{ operator.vision_id || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.tags') }}</div>
                <div class="field-value">
                  <span v-if="operator.tags && operator.tags.length">{{ operator.tags.map(t => t.name).join(', ') }}</span>
                  <span v-else>—</span>
                </div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.operatorGroups') }}</div>
                <div class="field-value">
                  <span v-if="operator.operator_groups && operator.operator_groups.length">{{ operator.operator_groups.map(g => g.name).join(', ') }}</span>
                  <span v-else>—</span>
                </div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.registeredAt') }}</div>
                <div class="field-value">{{ formattedCreatedAt || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.lastSeen') }}</div>
                <div class="field-value">{{ formattedLastSeen || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.uuid') }}</div>
                <div class="field-value small-value">{{ operator.uuid || '—' }}</div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="field">
                <div class="field-label">{{ $t('profiles.operatorView.fields.locations') }}</div>
                <div class="field-value">
                  <span v-if="operator.locations && operator.locations.length">{{ operator.locations.map(l => l.name).join(', ') }}</span>
                  <span v-else>—</span>
                </div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.externalId') }}</div>
                <div class="field-value">{{ operator.external_id || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.permissions') }}</div>
                <div class="field-value">{{ operator.permissions || $t('profiles.operatorView.values.allPermissions') }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.limits') }}</div>
                <div class="field-value">{{ operator.limits || $t('profiles.operatorView.values.noLimits') }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.registeredBy') }}</div>
                <div class="field-value">{{ operator.registered_by || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.lastSeenAt') }}</div>
                <div class="field-value">{{ operator.last_seen_location || '—' }}</div>
              </div>

              <div class="field mt-6">
                <div class="field-label">{{ $t('profiles.operatorView.fields.id') }}</div>
                <div class="field-value">{{ operator.id || '—' }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Custom fields -->
      <v-card class="panel-card mt-6" flat v-if="operatorCustomFields && operatorCustomFields.length">
        <v-card-text class="panel-inner">
          <div class="section-title">{{ $t('profiles.operatorView.sections.customFields') }}</div>

          <v-row class="mt-4">
            <v-col
              v-for="field in customFieldsData"
              :key="field.index"
              cols="12"
              md="4"
            >
              <div class="field">
                <div class="field-label">{{ field.alias }}</div>
                <div class="field-value">{{ formatCustomValue(field.value) }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FlyoutMenuHead from '../FlyoutMenuHead.vue';
import {  mdiCircle } from '@mdi/js';

export default {
  name: 'OperatorView',
  components: {
    FlyoutMenuHead
  },
  data() {
    return {
      updateTimer: null, // Track update timer
      apiRequests: [], // Track ongoing API requests for cancellation
      localError: null
    };
  },
  computed: {
    ...mapGetters({
      operator: 'operators/getOperator',
      loadingStates: 'operators/isLoading',
      errors: 'operators/getErrors'
    }),

    operatorUuid() {
      return this.$route.params.uuid;
    },

    loading() {
      return this.loadingStates?.operator || false;
    },

    error() {
      // prefer store error but fall back to local
      return this.errors?.operator || this.localError;
    },

    fullName() {
      if (!this.operator) return this.$t('profiles.operatorView.loading');
      const first = this.operator.first_name || '';
      const last = this.operator.last_name || '';
      const name = `${first} ${last}`.trim();
      return name || this.operator.username || this.$t('profiles.operatorView.operatorFallback');
    },

    initials() {
      if (!this.operator) return '';
      const first = (this.operator.first_name || '').charAt(0);
      const last = (this.operator.last_name || '').charAt(0);
      const fromName = `${first}${last}`.trim().toUpperCase();
      if (fromName) return fromName;
      // Fall back to first char of username
      return (this.operator.username || 'O').charAt(0).toUpperCase();
    },

    formattedDateOfBirth() {
      return this.formatDate(this.operator?.date_of_birth, { dateOnly: true });
    },

    formattedCreatedAt() {
      return this.formatDate(this.operator?.created_at);
    },

    formattedUpdatedAt() {
      return this.formatDate(this.operator?.updated_at);
    },

    formattedLastSeen() {
      return this.formatDate(this.operator?.last_seen_at);
    },

    online() {
      return this.operator?.online || false;
    },

    // Custom field definitions + values, read directly from the operator payload
    // (the backend embeds operator_custom_N / _alias / _enabled / _required / _unique
    // per operator, so this view doesn't need the separate space-config fetch).
    operatorCustomFields() {
      if (!this.operator) return [];

      const fields = [];
      for (let i = 1; i <= 8; i++) {
        const enabled = this.operator[`operator_custom_${i}_enabled`];
        const isEnabled = enabled === true || enabled === 'true' || enabled === 1 || enabled === '1';
        if (!isEnabled) continue;

        fields.push({
          index: i,
          alias: this.operator[`operator_custom_${i}_alias`] || `Custom ${i}`,
          required: !!this.operator[`operator_custom_${i}_required`],
          unique: !!this.operator[`operator_custom_${i}_unique`],
          value: this.operator[`operator_custom_${i}`]
        });
      }
      return fields;
    },

    // Only the custom fields that actually have a value - hide the rest entirely
    customFieldsData() {
      return this.operatorCustomFields.filter(
        f => f.value !== null && f.value !== undefined && f.value !== ''
      );
    },

    selectedItem() {
      if (!this.operator) {
        return {
          name: this.$t('profiles.operatorView.loadingOperator'),
          subtitle: '',
          statusIcon: mdiCircle,
          statusColor: 'grey',
          isOnline: false,
          initials: '',
          uuid: this.operatorUuid
        };
      }

      return {
        name: this.operator.username || this.$t('profiles.operatorView.operatorDetailsFallback'),
        subtitle: this.online ? this.$t('profiles.operatorView.online') : this.$t('profiles.operatorView.offline'),
        statusIcon: mdiCircle,
        statusColor: this.online ? 'success' : 'error',
        isOnline: this.online,
        initials: this.initials,
        uuid: this.operator.uuid
      };
    }
  },

  watch: {
    operatorUuid: {
      handler(newUuid, oldUuid) {
        if (newUuid && newUuid !== oldUuid) {
          this.loadOperator();
        }
      },
      immediate: false
    }
  },

  async mounted() {
    if (this.operatorUuid) {
      await this.loadOperator();

    }
  },


  methods: {
    ...mapActions({
      fetchOperator: 'operators/fetchOperator'
    }),

    async loadOperator() {
      this.localError = null;
      try {
        await this.fetchOperator(this.operatorUuid);
      } catch (err) {
        console.error('Failed to load operator', err);
        this.localError = err;
      }
    },

    async fetchOperatorDetails() {
      try {
        // Create abort controller for request cancellation
        const controller = new AbortController();
        this.apiRequests.push(controller);
        await this.fetchOperator(this.$route.params.uuid);
        // Remove completed request from tracking
        this.apiRequests = this.apiRequests.filter(req => req !== controller);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Fetch operator error:', error);
        }
      }
    },


    formatDate(value, opts = {}) {
      if (!value) return null;
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return null;
        if (opts.dateOnly) return d.toLocaleDateString();
        return d.toLocaleString();
      } catch (e) {
        return null;
      }
    },

    formatCustomValue(v) {
      // simple formatting for booleans / arrays
      if (v === true) return this.$t('common.yes');
      if (v === false) return this.$t('common.no');
      if (Array.isArray(v)) return v.join(', ');
      if (v === null || v === undefined || v === '') return '—';
      return v;
    }
  }
};
</script>

<style scoped>
.operator-view-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: transparent !important;
  color: var(--v-text-base);
}

.scrollable-container {
  max-height: calc(100vh - 115px);
  overflow-y: auto;
  padding-bottom: 50px;
}

/* Header */
.operator-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: transparent;
}

.operator-header-left {
  display: flex;
  flex-direction: column;
}

.operator-name {
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: -0.2px;
  color: var(--v-text-base);
}

.operator-status {
  display: flex;
  align-items: center;
  margin-top: 6px;
  color: var(--v-text-secondary);
  font-size: 0.95rem;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
  box-shadow: 0 0 0 4px rgba(0,0,0,0.12);
}
.status-dot.online { background-color: #20c997; }
.status-dot.offline { background-color: #6c757d; }

.operator-header-right {
  display: flex;
  align-items: center;
}

/* Avatar initials */
.avatar-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: white;
  font-weight: 600;
}

/* Content area */
.operator-content {
  flex: 1;
  overflow-y: auto;
  background: transparent !important;
}

/* Panel/card that matches the screenshot's inset rounded look */
.panel-card {
  background: var(--v-surface-base, #f5f5f5) !important;
  border-radius: 15px !important;
  box-shadow: none !important;
  border: 1px solid var(--v-divider-base, rgba(0,0,0,0.05)) !important;
}

.panel-card .v-card {
  border-radius: 20px !important;
}

/* Dark theme specific styles */
.theme--dark .panel-card {
  background: var(--v-surface-base, #2f3236) !important;
  border: 1px solid rgba(255,255,255,0.03) !important;
}

.panel-inner {
  padding: 22px;
  background: transparent !important;
}

/* Ensure v-card-text has transparent background */
.panel-card .v-card__text {
  background: transparent !important;
}

/* Section title (blue) */
.section-title {
  color: #2ea0ff; /* blue */
  font-weight: 600;
  margin-bottom: 6px;
}

/* Field layout: small label above a larger value */
.field {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
  margin-bottom: 8px;
  text-transform: none;
}

.field-value {
  font-size: 1rem;
  color: var(--v-text-base);
  font-weight: 500;
  word-break: break-word;
}

/* smaller / muted long values like uuid */
.small-value {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
}

/* spacing helpers */
.mt-4 { margin-top: 16px; }
.mt-6 { margin-top: 24px; }

/* Responsive tweaks */
@media (max-width: 960px) {
  .operator-content {
    padding: 16px;
  }

  .operator-name {
    font-size: 1.25rem;
  }
}
</style>
